import { PlayArrow } from '@mui/icons-material';
import {
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
} from '@mui/material';
import { useFormikContext } from 'formik';

import { useAudio } from '@frontend/hooks';
import { tOrder } from '@frontend/locales';
import { AllowedOrdersSortField } from '@frontend/modules/order/order-sort.model';
import {
  AllowedAudioTuneTypeValues,
  AllowedOrderCardSizeVariantTypeValues,
} from '@frontend/utils';

import { OrderListSettingsFormInput } from './order-list-settings-form-input.interface';

export const OrderListSettingsForm = () => {
  const { values, setFieldValue } =
    useFormikContext<OrderListSettingsFormInput>();
  const { sampleSound } = useAudio();

  return (
    <Stack spacing={3} my={4}>
      <FormControlLabel
        label={tOrder.fields.showItems()}
        labelPlacement="start"
        control={
          <Switch
            name="showItems"
            checked={values.showItems}
            color="success"
            onChange={(e) => setFieldValue('showItems', e.target.checked)}
          />
        }
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      />
      <Divider />
      <FormControlLabel
        label={tOrder.fields.showGuestName()}
        name="showGuestName"
        labelPlacement="start"
        control={
          <Switch
            checked={values.showGuestName}
            color="success"
            onChange={(e) => setFieldValue('showGuestName', e.target.checked)}
          />
        }
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      />
      <Divider />

      <FormControlLabel
        label={tOrder.fields.showTimeParameters()}
        name="showTimeParameters"
        labelPlacement="start"
        control={
          <Switch
            checked={values.showTimeParameters}
            color="success"
            onChange={(e) =>
              setFieldValue('showTimeParameters', e.target.checked)
            }
          />
        }
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      />
      <Divider />

      <FormControlLabel
        label={tOrder.fields.sortOrdersBy()}
        labelPlacement="start"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        control={
          <RadioGroup
            name="sortField"
            row
            value={values.sortField}
            onChange={(e) => setFieldValue('sortField', e.target.value)}
          >
            {AllowedOrdersSortField.map((sortField) => (
              <FormControlLabel
                key={`sort-field-${sortField}`}
                value={sortField}
                control={<Radio color="default" />}
                label={
                  sortField === 'TIME'
                    ? tOrder.fields.sortByTime()
                    : tOrder.fields.sortByVerification()
                }
              />
            ))}
          </RadioGroup>
        }
      />
      <Divider />

      <FormControlLabel
        label={tOrder.fields.itemFontSize()}
        labelPlacement="start"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        control={
          <RadioGroup
            name="itemFontSize"
            row
            value={values.itemFontSize}
            onChange={(e) => setFieldValue('itemFontSize', e.target.value)}
          >
            {AllowedOrderCardSizeVariantTypeValues.map((size) => (
              <FormControlLabel
                key={`item-font-size-${size}`}
                value={size}
                control={<Radio color="default" />}
                label={tOrder.orderCardSizeOptions[size]()}
              />
            ))}
          </RadioGroup>
        }
      />
      <Divider />
      <FormControlLabel
        label={tOrder.fields.cardSize()}
        labelPlacement="start"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        control={
          <RadioGroup
            name="cardSize"
            row
            value={values.cardSize}
            onChange={(e) => setFieldValue('cardSize', e.target.value)}
          >
            {AllowedOrderCardSizeVariantTypeValues.map((size) => (
              <FormControlLabel
                key={`card-size-${size}`}
                value={size}
                control={<Radio color="default" />}
                label={tOrder.orderCardSizeOptions[size]()}
              />
            ))}
          </RadioGroup>
        }
      />
      <Divider />
      <FormControlLabel
        label={tOrder.fields.newOrderSound()}
        labelPlacement="start"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        control={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              onClick={() => {
                sampleSound(values.tune);
              }}
              startIcon={<PlayArrow />}
            >
              {tOrder.actions.play()}
            </Button>
            <Select
              name="tune"
              value={values.tune}
              onChange={(e) => setFieldValue('tune', e.target.value)}
              sx={{ width: '250px' }}
            >
              {AllowedAudioTuneTypeValues.map((tune) => (
                <MenuItem key={`tune-${tune}`} value={tune}>
                  {tOrder.tunes[tune]()}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        }
      />
    </Stack>
  );
};
